/**
 * Lawn Synergy cart drawer.
 *
 * Responsibilities:
 * - Open and close the drawer.
 * - Update line-item quantities through Shopify's Ajax Cart API.
 * - Refresh the full drawer after external product/app additions.
 * - Preserve focus and publish standard cart-update signals.
 * - Remove only exact duplicate Selleasy offer branches.
 */

function getSelleasyWidgetSignature(widget) {
  if (!(widget instanceof HTMLElement)) return '';

  const widgetId = widget.getAttribute('widget') || '';
  const offerId = widget.getAttribute('offer') || '';
  const triggerProductId = widget.getAttribute('lb-trigger-product-id') || '';

  if (!widgetId || !offerId) return '';

  return `${widgetId}::${offerId}::${triggerProductId}`;
}

function removeDuplicateSelleasyWidgets(cartDrawer) {
  const widgets = [...cartDrawer.querySelectorAll('lb-upsell-widget[widget][offer]')];
  if (widgets.length < 2) return;

  const seen = new Set();

  for (const widget of widgets) {
    if (!widget.isConnected) continue;

    const signature = getSelleasyWidgetSignature(widget);
    if (!signature) continue;

    if (seen.has(signature)) {
      widget.remove();
      continue;
    }

    seen.add(signature);
  }
}

function scheduleSelleasyCleanup(cartDrawer) {
  [0, 100, 300, 700, 1500, 3000].forEach((delay) => {
    window.setTimeout(() => removeDuplicateSelleasyWidgets(cartDrawer), delay);
  });
}

function observeSelleasyOffers(cartDrawer) {
  if (!cartDrawer.selleasyObserver) {
    let cleanupScheduled = false;

    cartDrawer.selleasyObserver = new MutationObserver((mutations) => {
      const hasWidgetChange = mutations.some((mutation) => {
        return [...mutation.addedNodes].some((node) => {
          if (!(node instanceof Element)) return false;

          return node.matches?.('lb-upsell-widget[widget][offer]')
            || Boolean(node.querySelector?.('lb-upsell-widget[widget][offer]'));
        });
      });

      if (!hasWidgetChange || cleanupScheduled) return;
      cleanupScheduled = true;

      window.requestAnimationFrame(() => {
        cleanupScheduled = false;
        removeDuplicateSelleasyWidgets(cartDrawer);
      });
    });

    /*
     * Selleasy may inject its custom element anywhere inside the drawer and may
     * do so after its surrounding wrapper is already present. Observe the cart
     * drawer itself, then remove only a second widget with the same stable app
     * identifiers: widget, offer, and trigger product.
     */
    cartDrawer.selleasyObserver.observe(cartDrawer, {
      childList: true,
      subtree: true,
    });
  }

  scheduleSelleasyCleanup(cartDrawer);
}

class LawnSynergyCartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;
    this.quantityTimer = null;
    this.isUpdating = false;

    this.handleClick = this.handleClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.hidden = false;

    this.addEventListener('click', this.handleClick);
    this.addEventListener('change', this.handleChange);
    this.addEventListener('keydown', this.handleKeydown);

    this.bindCartTriggers();
    this.setupCartNote();
    observeSelleasyOffers(this);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('change', this.handleChange);
    this.removeEventListener('keydown', this.handleKeydown);
    window.clearTimeout(this.quantityTimer);
    this.selleasyObserver?.disconnect();
  }

  bindCartTriggers() {
    document.querySelectorAll('#cart-icon-bubble, [data-cart-icon-bubble]').forEach((trigger) => {
      if (trigger.dataset.lsCartDrawerBound === 'true') return;

      trigger.dataset.lsCartDrawerBound = 'true';
      trigger.setAttribute('aria-haspopup', 'dialog');

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(trigger);
      });
    });
  }

  handleClick(event) {
    const closeTrigger = event.target.closest('#CartDrawer-Overlay, [data-cart-drawer-close]');
    if (closeTrigger) {
      event.preventDefault();
      this.close();
      return;
    }

    const quantityButton = event.target.closest('[data-cart-quantity-action]');
    if (quantityButton) {
      event.preventDefault();
      this.changeQuantityFromButton(quantityButton);
      return;
    }

    const removeButton = event.target.closest('[data-cart-remove]');
    if (removeButton) {
      event.preventDefault();
      this.updateQuantity(removeButton.dataset.line, 0, removeButton);
    }
  }

  handleChange(event) {
    const input = event.target.closest('[data-cart-quantity-input]');
    if (!input) return;

    const quantity = this.normalizeQuantity(input.value);
    input.value = quantity;

    window.clearTimeout(this.quantityTimer);
    this.quantityTimer = window.setTimeout(() => {
      this.updateQuantity(input.dataset.line, quantity, input);
    }, 250);
  }

  handleKeydown(event) {
    if (event.key === 'Escape') this.close();
  }

  normalizeQuantity(value) {
    return Math.max(0, Number.parseInt(value, 10) || 0);
  }

  changeQuantityFromButton(button) {
    const quantityControl = button.closest('[data-cart-quantity]');
    const input = quantityControl?.querySelector('[data-cart-quantity-input]');
    if (!input) return;

    const currentQuantity = this.normalizeQuantity(input.value);
    const quantityChange = button.dataset.cartQuantityAction === 'increase' ? 1 : -1;
    const nextQuantity = Math.max(0, currentQuantity + quantityChange);

    if (nextQuantity === currentQuantity) return;

    input.value = nextQuantity;
    this.updateQuantity(input.dataset.line, nextQuantity, button);
  }

  async updateQuantity(line, quantity, trigger) {
    if (!line || this.isUpdating) return;

    this.isUpdating = true;
    this.clearError();
    this.setLoadingState(line, true);

    try {
      const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          line: Number(line),
          quantity: Number(quantity),
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });

      const cart = await response.json();

      if (!response.ok || cart.errors || cart.status >= 400) {
        const message = [cart.description, cart.message, cart.errors]
          .find((value) => typeof value === 'string' && value.trim());
        throw new Error(message || 'Unable to update cart. Please try again.');
      }

      const actualQuantity = cart.items?.[Number(line) - 1]?.quantity;
      const wasQuantityLimited = Number(quantity) > 0
        && Number.isFinite(actualQuantity)
        && actualQuantity < Number(quantity);

      this.renderCart(cart, trigger);

      if (wasQuantityLimited) {
        this.showError(line, `Only ${actualQuantity} available for this item. Quantity was adjusted.`);
      }
    } catch (error) {
      const input = this.querySelector(`[data-cart-line="${line}"] [data-cart-quantity-input]`);
      if (input) input.value = input.defaultValue;
      this.showError(line, error.message || 'Unable to update cart. Please try again.');
      this.setLoadingState(line, false);
    } finally {
      this.isUpdating = false;
    }
  }

  renderCart(cart, trigger = null) {
    const drawerSection = cart.sections?.['cart-drawer'];
    if (!drawerSection) {
      window.location.reload();
      return;
    }

    const parsedDocument = new DOMParser().parseFromString(drawerSection, 'text/html');
    const sourceDrawer = parsedDocument.querySelector('#CartDrawer');
    const currentDrawer = this.querySelector('#CartDrawer');

    if (!sourceDrawer || !currentDrawer) {
      window.location.reload();
      return;
    }

    const wasEmpty = this.classList.contains('is-empty');
    const isEmpty = cart.item_count === 0;
    const isExternalCartRender = trigger === null;
    const focusLine = trigger?.closest('[data-cart-line]')?.dataset.cartLine;
    const focusAction = trigger?.dataset.cartQuantityAction;
    const wasRemoveAction = trigger?.matches('[data-cart-remove]') === true;

    /*
     * Product forms and app additions call renderContents without a drawer
     * control. Use fresh complete drawer markup for those changes so Selleasy
     * recalculates offer eligibility. Internal quantity/remove actions update
     * only cart-owned regions to preserve a stable interaction and focus state.
     */
    if (isExternalCartRender || wasEmpty !== isEmpty) {
      if (!isEmpty) {
        const existingProtectionTarget = currentDrawer.querySelector('.cart-drawer__shipping-protection-target');
        const freshProtectionTarget = sourceDrawer.querySelector('.cart-drawer__shipping-protection-target');

        if (existingProtectionTarget && freshProtectionTarget) {
          freshProtectionTarget.replaceWith(existingProtectionTarget);
        }
      }

      currentDrawer.replaceWith(sourceDrawer);
    } else {
      this.replaceRegion(parsedDocument, '#CartDrawer-CartItems');
      this.replaceRegion(parsedDocument, '.ls-cart-checkout__summary');
    }

    const responseItemCount = Number.parseInt(cart.item_count, 10);
    const renderedItemCount = Number.parseInt(sourceDrawer.dataset.cartItemCount, 10);
    const itemCount = Number.isFinite(responseItemCount) ? responseItemCount : renderedItemCount;

    this.classList.toggle('is-empty', itemCount === 0);
    this.updateCartBubble(cart.sections?.['cart-icon-bubble'], itemCount);
    this.setupCartNote();
    this.bindCartTriggers();
    this.open();

    this.restoreFocus(focusLine, focusAction, isEmpty, wasRemoveAction);
    this.publishCartUpdate(cart);
    observeSelleasyOffers(this);
  }

  replaceRegion(parsedDocument, selector) {
    const source = parsedDocument.querySelector(selector);
    const target = this.querySelector(selector);

    if (source && target) target.replaceWith(source);
  }

  updateCartBubble(sectionHtml, itemCount = 0) {
    const count = Math.max(0, Number.parseInt(itemCount, 10) || 0);
    const targets = document.querySelectorAll('[data-cart-icon-bubble]');

    targets.forEach((target) => {
      const emptyIcon = target.querySelector('[data-cart-icon-empty]');
      const filledIcon = target.querySelector('[data-cart-icon-filled]');

      if (emptyIcon) emptyIcon.hidden = count > 0;
      if (filledIcon) filledIcon.hidden = count === 0;

      let bubble = target.querySelector('[data-cart-count-bubble]');

      if (count === 0) {
        bubble?.remove();
        target.setAttribute('aria-label', 'Cart, empty');
        return;
      }

      if (!bubble) {
        bubble = document.createElement('span');
        bubble.className = 'cart-count-bubble';
        bubble.setAttribute('data-cart-count-bubble', '');
        bubble.innerHTML = '<span aria-hidden="true" data-cart-count></span><span class="visually-hidden" data-cart-count-label></span>';

        const iconWrap = target.querySelector('[data-cart-icon-wrap]') || target;
        iconWrap.appendChild(bubble);
      }

      const visibleCount = bubble.querySelector('[data-cart-count]');
      const accessibleCount = bubble.querySelector('[data-cart-count-label]');

      if (visibleCount) visibleCount.textContent = count < 100 ? String(count) : '99+';
      if (accessibleCount) accessibleCount.textContent = `${count} ${count === 1 ? 'item' : 'items'} in cart`;

      target.setAttribute('aria-label', `Cart, ${count} ${count === 1 ? 'item' : 'items'}`);
    });
  }

  restoreFocus(line, action, isEmpty, wasRemoveAction = false) {
    window.requestAnimationFrame(() => {
      if (wasRemoveAction) {
        this.querySelector('.ls-cart-drawer__panel')?.focus({ preventScroll: true });
        return;
      }

      if (!line || isEmpty) return;

      const selector = action
        ? `[data-cart-line="${line}"] [data-cart-quantity-action="${action}"]`
        : `[data-cart-line="${line}"] [data-cart-quantity-input]`;

      this.querySelector(selector)?.focus({ preventScroll: true });
    });
  }

  publishCartUpdate(cart) {
    window.requestAnimationFrame(() => {
      const detail = {
        cart,
        cartData: cart,
        source: 'cart-drawer',
      };

      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail,
      }));

      if (
        typeof window.publish === 'function'
        && window.PUB_SUB_EVENTS?.cartUpdate
      ) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, detail);
      } else if (
        typeof publish === 'function'
        && typeof PUB_SUB_EVENTS !== 'undefined'
        && PUB_SUB_EVENTS.cartUpdate
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, detail);
      }
    });
  }

  setLoadingState(line, isLoading) {
    const item = this.querySelector(`[data-cart-line="${line}"]`);
    if (!item) return;

    item.classList.toggle('is-updating', isLoading);
    item.setAttribute('aria-busy', isLoading ? 'true' : 'false');

    item.querySelectorAll('button, input').forEach((control) => {
      control.disabled = isLoading;
    });
  }

  clearError() {
    this.querySelectorAll('[data-cart-line-error]').forEach((error) => {
      error.textContent = '';
      error.hidden = true;
    });
    this.querySelectorAll('[data-cart-quantity-input]').forEach((input) => {
      input.removeAttribute('aria-invalid');
      if (input.getAttribute('aria-describedby')?.startsWith('CartDrawer-LineItemError-')) {
        input.removeAttribute('aria-describedby');
      }
    });
    const globalError = this.querySelector('#CartDrawer-CartErrors');
    if (globalError) globalError.textContent = '';
  }

  showError(line, message) {
    const item = this.querySelector(`[data-cart-line="${line}"]`);
    const error = item?.querySelector('[data-cart-line-error]');
    if (error) {
      error.textContent = message;
      error.hidden = false;
      const input = item.querySelector('[data-cart-quantity-input]');
      if (input) {
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', error.id);
      }
      return;
    }
    const globalError = this.querySelector('#CartDrawer-CartErrors');
    if (globalError) globalError.textContent = message;
  }

  open(triggeredBy = null) {
    if (triggeredBy) this.activeElement = triggeredBy;

    const wasOpen = this.classList.contains('active');

    this.classList.add('active');
    document.body.classList.add('overflow-hidden');

    if (!wasOpen) {
      document.dispatchEvent(new CustomEvent('shopify:cart-drawer-open', {
        detail: { source: 'cart-drawer' },
      }));
    }

    observeSelleasyOffers(this);

    const panel = this.querySelector('.ls-cart-drawer__panel');
    if (panel && typeof trapFocus === 'function') {
      window.requestAnimationFrame(() => trapFocus(panel, panel));
    }
  }

  close() {
    if (!this.classList.contains('active')) return;

    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    } else {
      this.activeElement?.focus?.();
    }
  }

  setupCartNote() {
    const summary = this.querySelector('#Details-CartDrawer > summary');
    if (!summary || summary.dataset.lsAccessible === 'true') return;

    summary.dataset.lsAccessible = 'true';
    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', summary.parentElement.open ? 'true' : 'false');

    summary.addEventListener('click', () => {
      window.requestAnimationFrame(() => {
        summary.setAttribute('aria-expanded', summary.parentElement.open ? 'true' : 'false');
      });
    });
  }

  /* Compatibility methods used by Dawn product forms and app integrations. */
  renderContents(cart) {
    this.renderCart(cart);
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML || '';
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', LawnSynergyCartDrawer);
}
